import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Save, RefreshCw, ArrowLeft, Wrench } from 'lucide-react';

const AdminInstallationPricing = () => {
  const navigate = useNavigate();
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editedPrices, setEditedPrices] = useState({});
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const widthRanges = [
    { key: 'price_0_180', label: '0 - 1,80 m' },
    { key: 'price_181_380', label: '1,81 - 3,80 m' },
    { key: 'price_381_580', label: '3,81 - 5,80 m' },
    { key: 'price_581_780', label: '5,81 - 7,80 m' },
    { key: 'price_781_980', label: '7,81 - 9,80 m' },
    { key: 'price_981_1200', label: '9,81 - 12 m' }
  ];

  const fetchPrices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/installation-pricing/`);
      setPrices(response.data);
      const initial = {};
      response.data.forEach(p => { initial[p.store_type] = { ...p }; });
      setEditedPrices(initial);
      setLoading(false);
    } catch (err) { setError('Erreur lors du chargement des prix'); setLoading(false); }
  }, [BACKEND_URL]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    fetchPrices();
  }, [navigate, fetchPrices]);

  const handlePriceChange = (storeType, priceKey, value) => {
    setEditedPrices(prev => ({ ...prev, [storeType]: { ...prev[storeType], [priceKey]: parseFloat(value) || 0 } }));
  };

  const handleSave = async (storeType) => {
    try {
      setSaving(true); setError(''); setSuccess('');
      const token = localStorage.getItem('admin_token');
      const priceData = editedPrices[storeType];
      await axios.put(`${BACKEND_URL}/api/installation-pricing/${storeType}`, {
        price_0_180: priceData.price_0_180, price_181_380: priceData.price_181_380, price_381_580: priceData.price_381_580,
        price_581_780: priceData.price_581_780, price_781_980: priceData.price_781_980, price_981_1200: priceData.price_981_1200
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess(`Prix mis à jour pour "${priceData.label}"`);
      setTimeout(() => setSuccess(''), 3000);
      setSaving(false);
    } catch (err) { setError('Erreur lors de la sauvegarde'); setSaving(false); }
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true); setError(''); setSuccess('');
      const token = localStorage.getItem('admin_token');
      for (const storeType of Object.keys(editedPrices)) {
        const priceData = editedPrices[storeType];
        await axios.put(`${BACKEND_URL}/api/installation-pricing/${storeType}`, {
          price_0_180: priceData.price_0_180, price_181_380: priceData.price_181_380, price_381_580: priceData.price_381_580,
          price_581_780: priceData.price_581_780, price_781_980: priceData.price_781_980, price_981_1200: priceData.price_981_1200
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      setSuccess('Tous les prix ont été mis à jour');
      setTimeout(() => setSuccess(''), 3000);
      setSaving(false);
    } catch (err) { setError('Erreur lors de la sauvegarde'); setSaving(false); }
  };

  const handleResetDefaults = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir réinitialiser tous les prix aux valeurs par défaut ?')) return;
    try {
      setSaving(true);
      await axios.post(`${BACKEND_URL}/api/installation-pricing/reset-defaults`);
      await fetchPrices();
      setSuccess('Prix réinitialisés aux valeurs par défaut');
      setTimeout(() => setSuccess(''), 3000);
      setSaving(false);
    } catch (err) { setError('Erreur lors de la réinitialisation'); setSaving(false); }
  };

  if (loading) return <div className="min-h-screen bg-gray-100 flex items-center justify-center"><div className="text-gray-600">Chargement...</div></div>;

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg"><ArrowLeft className="w-5 h-5" /></button>
            <div className="flex items-center gap-3"><Wrench className="w-6 h-6 text-brand" /><h1 className="text-xl font-bold text-gray-900">Tarifs Installation / Pose</h1></div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleResetDefaults} disabled={saving} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2"><RefreshCw className="w-4 h-4" />Réinitialiser</button>
            <button onClick={handleSaveAll} disabled={saving} className="px-4 py-2 bg-brand text-white rounded-lg hover:opacity-90 flex items-center gap-2"><Save className="w-4 h-4" />{saving ? 'Sauvegarde...' : 'Tout sauvegarder'}</button>
          </div>
        </div>
      </header>
      {error && <div className="max-w-7xl mx-auto px-4 mt-4"><div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div></div>}
      {success && <div className="max-w-7xl mx-auto px-4 mt-4"><div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">{success}</div></div>}
      <div className="max-w-7xl mx-auto px-4 mt-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-blue-800 text-sm"><strong>Note :</strong> Ces prix sont les tarifs de base pour l'installation. Une majoration de <strong>+20%</strong> est automatiquement appliquée si l'installation est en étage et/ou si le store est positionné à plus de 3 mètres du sol.</p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 pb-8">
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 sticky left-0 bg-gray-50 min-w-[250px]">Type de store</th>
                  {widthRanges.map(range => (<th key={range.key} className="px-4 py-3 text-center text-sm font-semibold text-gray-900 min-w-[120px]">{range.label}</th>))}
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 min-w-[100px]">Action</th>
                </tr>
              </thead>
              <tbody>
                {prices.map((price, index) => (
                  <tr key={price.store_type} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 sticky left-0 bg-inherit">{price.label}</td>
                    {widthRanges.map(range => (
                      <td key={range.key} className="px-4 py-3 text-center">
                        <div className="relative">
                          <input type="number" step="0.01" value={editedPrices[price.store_type]?.[range.key] || ''} onChange={(e) => handlePriceChange(price.store_type, range.key, e.target.value)} className="w-24 px-2 py-1 text-center border border-gray-200 rounded focus:border-brand focus:outline-none" />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">€</span>
                        </div>
                      </td>
                    ))}
                    <td className="px-4 py-3 text-center"><button onClick={() => handleSave(price.store_type)} disabled={saving} className="px-3 py-1 bg-brand text-white text-sm rounded hover:opacity-90"><Save className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminInstallationPricing;
